import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import { ISearchCity } from '../../../contexts/Search/types';
import getFlag from '../../../data/flags';
import AnimatedTextField from '../../Forms/Input/AnimatedTextField';
import {
  PanelScrollableWrapper,
  PanelWrapper,
} from '../../Funnel/Panel/common';
import { MainPanel } from '../../Funnel/Panel/MainPanel';
import {
  TSB_CITY_TEST_ID,
  TSB_DESTINATION_CITIES_PANEL_TEST_ID,
  TSB_ORIGIN_CITIES_PANEL_TEST_ID,
  TSB_POPULAR_CITIES_TEST_ID,
  TSB_SUGGESTED_CITIES_TEST_ID,
} from '../constants';
import messages from '../messages';

const InputWrapper = styled(PanelWrapper)`
  ${tw`md:hidden pb-medium`}
  padding-top: 2px;
`;
interface CitiesPanelProps {
  onClose: () => void;
  onSelectCity: (city: ISearchCity) => void;

  suggestedCities: ISearchCity[];
  popularCities: ISearchCity[];
  city: string;
  isOrigin?: boolean;

  onCityChange: (city: string) => void;
  hoveredCityIndex: number;
  closePanelButtonRef: React.MutableRefObject<HTMLButtonElement>;
}

/**
 * @description Returns a component that displays a panel for editing the selected cities.
 * @returns {React.FC<CitiesPanelProps>}
 */
const CitiesPanel: React.FC<CitiesPanelProps> = ({
  city,
  closePanelButtonRef,
  hoveredCityIndex,
  isOrigin,
  onCityChange,
  onClose,
  onSelectCity,
  popularCities,
  suggestedCities,
}) => {
  return (
    <MainPanel
      closeButtonRef={closePanelButtonRef}
      onClose={() => onClose()}
      testid={
        isOrigin
          ? TSB_ORIGIN_CITIES_PANEL_TEST_ID
          : TSB_DESTINATION_CITIES_PANEL_TEST_ID
      }
      title={
        isOrigin ? (
          <FormattedMessage {...messages.citiesPanelDepartureOverlayTitle} />
        ) : (
          <FormattedMessage {...messages.citiesPanelArrivalOverlayTitle} />
        )
      }
    >
      <InputWrapper>
        <AnimatedTextField
          autoFocus
          onChange={(e) => onCityChange(e.target.value)}
          onFocus={(e) => e.target.select()}
          placeHolderLabel={
            isOrigin ? (
              <FormattedMessage {...messages.departureCity} />
            ) : (
              <FormattedMessage {...messages.arrivalCity} />
            )
          }
          value={city}
        />
      </InputWrapper>

      {suggestedCities ? (
        <ResultsCities
          cities={suggestedCities}
          hoveredCityIndex={hoveredCityIndex}
          onSelectCity={(city) => onSelectCity(city)}
        />
      ) : (
        <PopularCities
          cities={popularCities}
          hoveredCityIndex={hoveredCityIndex}
          onSelectCity={(city) => onSelectCity(city)}
        />
      )}
    </MainPanel>
  );
};

const CitiesListContainer = styled.ul`
  ${tw`p-0 m-0 flex flex-col`}
  list-style: none;
  gap: var(--size-small);
`;

const City = styled.li<{ isHovered: boolean }>`
  ${tw`flex justify-between py-tiny px-medium2 cursor-pointer
      py-small
    hover:bg-grey-100
  `}
  gap:var(--size-small);
  ${({ isHovered }) => isHovered && tw`bg-grey-100`}
`;

const Details = styled.div`
  ${tw`flex flex-col`}
`;

const CityName = styled.span`
  ${tw`text-p-medium-m leading-p-medium-m text-text 
    lg:text-p-small lg:leading-p-small
  `}
`;

const NearCity = styled.span`
  ${tw`text-p-xsmall-m leading-p-xsmall-m text-textLight
    lg:text-p-xsmall lg:leading-p-xsmall
  `}
`;

const CityFlag = styled.span`
  ${tw`text-p-small-m leading-p-small-m text-textLight flex items-center 
    lg:text-p-xsmall lg:leading-p-xsmall
  `}
  gap:var(--size-small);
`;

const Flag = styled.img`
  width: 16px;
  border-radius: 2px;
`;

const NoResults = styled.span`
  ${tw`px-medium2 text-p-medium-m leading-p-medium-m text-text 
  lg:text-p-small lg:leading-p-small
  `}
`;

interface CitiesListProps {
  cities: ISearchCity[];
  onSelectCity: (city: ISearchCity) => void;
  hoveredCityIndex: number;
}

/**
 * @description Returns a list of cities
 * @returns {React.FC<CitiesListProps>}
 */
const CitiesList: React.FC<CitiesListProps> = ({
  cities,
  hoveredCityIndex,
  onSelectCity,
}) => (
  <CitiesListContainer>
    {cities.length === 0 && (
      <NoResults>
        <FormattedMessage {...messages.citiesPanelNoResults} />
      </NoResults>
    )}
    {cities.map((city, index) => {
      const flag = getFlag(city.country);
      return (
        <City
          key={city.id}
          data-testid={TSB_CITY_TEST_ID}
          isHovered={hoveredCityIndex === index}
          onClick={() => onSelectCity(city)}
        >
          <Details>
            <CityName>{city.name}</CityName>
            {city.nearFrom && (
              <NearCity>
                (à {city.nearFrom.distance}km de {city.nearFrom.name})
              </NearCity>
            )}

            {city.stations.length > 0 && (
              <NearCity>
                {city.stations.reduce(
                  (previous, station, index) => (
                    <React.Fragment key={index}>
                      {previous}
                      {index > 0 && <span>, </span>}
                      {station.name}
                    </React.Fragment>
                  ),
                  <React.Fragment></React.Fragment>,
                )}
              </NearCity>
            )}
          </Details>

          <CityFlag>
            {city.country}{' '}
            {flag && <Flag alt={`${city.country} flag`} src={flag} />}
          </CityFlag>
        </City>
      );
    })}
  </CitiesListContainer>
);

const ResultsContainer = styled.div`
  ${tw`py-medium flex flex-col max-h-full overflow-hidden`}
  gap:var(--size-medium);
`;

const PopularTitle = styled.h5`
  ${tw`font-title-medium-m leading-title-medium-m m-0`}
`;

const ScrollableWrapper = styled(PanelScrollableWrapper)`
  ${tw`px-0`}
`;

/**
 * @description Returns a view displaying popular cities
 * @returns {React.FC<CitiesListProps>}
 */
const PopularCities: React.FC<CitiesListProps> = ({
  cities,
  hoveredCityIndex,
  onSelectCity,
}) => (
  <ResultsContainer data-testid={TSB_POPULAR_CITIES_TEST_ID}>
    <PanelWrapper>
      <PopularTitle>
        <FormattedMessage {...messages.mostFrequent} />
      </PopularTitle>
    </PanelWrapper>

    {cities && cities.length === 0 && (
      <NoResults>
        <FormattedMessage {...messages.citiesPanelNoResults} />
      </NoResults>
    )}

    {cities && (
      <ScrollableWrapper>
        <CitiesList
          cities={cities}
          hoveredCityIndex={hoveredCityIndex}
          onSelectCity={onSelectCity}
        />
      </ScrollableWrapper>
    )}
  </ResultsContainer>
);

const ResultsCitiesContainer = styled(ResultsContainer)`
  border-top: 1px solid;
  ${tw` border-grey-300`};
`;

/**
 * @description Returns a view displaying a list of cities
 * @returns {React.FC<CitiesListProps>}
 */
const ResultsCities: React.FC<CitiesListProps> = ({
  cities,
  hoveredCityIndex,
  onSelectCity,
}) => (
  <ResultsCitiesContainer data-testid={TSB_SUGGESTED_CITIES_TEST_ID}>
    {cities && (
      <ScrollableWrapper>
        <CitiesList
          cities={cities}
          hoveredCityIndex={hoveredCityIndex}
          onSelectCity={onSelectCity}
        />
      </ScrollableWrapper>
    )}
  </ResultsCitiesContainer>
);

export default CitiesPanel;
